import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import { api, tryRefresh } from './api-client';
import type { MeResponse } from '@produzionejb/types';

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clear = useAuthStore((s) => s.clear);
  const location = useLocation();

  useEffect(() => {
    // Bootstrap solo dallo stato iniziale 'idle'.
    // NON chiamiamo setStatus('loading') prima della fetch: settare lo stato
    // qui causerebbe un re-render -> dep change -> cleanup -> cancelled=true,
    // e poi quando la fetch completa il check `if (!cancelled)` la blocca.
    // Risultato del bug: status restava 'loading' per sempre. Soluzione:
    // resta in 'idle' durante la fetch (mostrato come "Loading..." nel render
    // condizionale sotto) e passa direttamente a 'authenticated' o 'anonymous'
    // alla risoluzione.
    if (status !== 'idle') return;
    let cancelled = false;

    void (async () => {
      try {
        // tryRefresh deduplica chiamate parallele a /api/auth/refresh:
        // se l'app fa N chiamate API non autenticate in parallelo, solo UNA
        // colpisce /refresh, evitando il backend rotation che revocherebbe
        // la sessione tra una refresh e l'altra.
        const accessToken = await tryRefresh();
        if (!accessToken) throw new Error('No active session');
        const me = await api<MeResponse>('/api/auth/me');
        if (cancelled) return;
        setAuth({ accessToken, user: me });
      } catch {
        if (!cancelled) clear();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, setAuth, clear]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">Loading…</div>
    );
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
